import { useEffect, useRef, useState, useCallback } from 'react';
import { Device as TwilioDevice } from '@twilio/voice-sdk';

import { useAppSelector } from '../app/hooks';
import TwilioVoiceService from '../Services/TwilioVoiceService';
import AudioAnalysisService, { AudioLevels } from '../Services/AudioAnalysisService';

export interface UseAudioWaveformReturn {
  microphoneLevel: number;
  phoneLevel: number;
  combinedLevel: number;
  frequencyData: Uint8Array | null;
  isActive: boolean;
  error: string | null;
}

export const useAudioWaveform = (): UseAudioWaveformReturn => {
  const [audioLevels, setAudioLevels] = useState<AudioLevels>({
    microphone: 0,
    phone: 0,
    combined: 0,
  });
  const [frequencyData, setFrequencyData] = useState<Uint8Array | null>(null);
  const [isActive, setIsActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const audioAnalysisServiceRef = useRef<AudioAnalysisService | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  const callBarState = useAppSelector((state: any) => state.callCentre.callBarState);
  const currentActiveCallRecordId = useAppSelector((state: any) => state.callCentre.currentActiveCallRecordId);
  const audioWaveformActive = useAppSelector((state: any) => state.callCentre.audioWaveformActive);
  const isCallInProgress = callBarState === 'IN_PROGRESS' || callBarState === 'CONNECTED';

  // Initialize audio analysis service
  const initializeAudioAnalysis = useCallback(async () => {
    try {
      if (!TwilioVoiceService.callConnection) {
        setError('No active call connection');
        return;
      }

      // Get audio context from Twilio device
      const audioContext = TwilioDevice.audioContext;
      if (!audioContext) {
        setError('Audio context not available');
        return;
      }

      // Create audio analysis service
      const audioAnalysisService = new AudioAnalysisService({
        fftSize: 128, // Reduced from 256 for better performance
        smoothingTimeConstant: 0.8,
        minDecibels: -90,
        maxDecibels: -10,
        updateInterval: 32, // Reduced frequency for better performance (~30fps)
      });

      audioAnalysisService.initialize(audioContext);

      // Get microphone stream
      const microphoneStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioAnalysisService.startMicrophoneAnalysis(microphoneStream);

      // Get phone stream from Twilio connection
      const phoneStream = TwilioVoiceService.callConnection?._mediaHandler?.pcStream;
      if (phoneStream instanceof MediaStream) {
        audioAnalysisService.startPhoneAnalysis(phoneStream);
      }

      // Subscribe to audio level updates
      const unsubscribe = audioAnalysisService.subscribe((levels: AudioLevels) => {
        setAudioLevels(levels);
        setFrequencyData(audioAnalysisService.getFrequencyData());
        setIsActive(true);
        setError(null);
      });

      audioAnalysisServiceRef.current = audioAnalysisService;
      unsubscribeRef.current = unsubscribe;

    } catch (err) {
      console.error('Failed to initialize audio analysis:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setIsActive(false);
    }
  }, []);

  // Cleanup audio analysis
  const cleanupAudioAnalysis = useCallback(() => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    if (audioAnalysisServiceRef.current) {
      audioAnalysisServiceRef.current.stop();
      audioAnalysisServiceRef.current = null;
    }

    setAudioLevels({ microphone: 0, phone: 0, combined: 0 });
    setFrequencyData(null);
    setIsActive(false);
    setError(null);
  }, []);

  // Start/stop audio analysis based on call state
  useEffect(() => {
    if (audioWaveformActive && isCallInProgress && currentActiveCallRecordId) {
      initializeAudioAnalysis();
    } else {
      cleanupAudioAnalysis();
    }

    return cleanupAudioAnalysis;
  }, [audioWaveformActive,isCallInProgress, currentActiveCallRecordId, initializeAudioAnalysis, cleanupAudioAnalysis]);

  // Handle call connection changes
  useEffect(() => {
    const handleConnectionChange = () => {
      if (audioWaveformActive && isCallInProgress && audioAnalysisServiceRef.current) {
        // Reinitialize with new connection
        cleanupAudioAnalysis();
        setTimeout(() => {
          initializeAudioAnalysis();
        }, 100);
      }
    };

    // Listen for connection state changes
    if (TwilioVoiceService.callConnection) {
      TwilioVoiceService.callConnection.on('disconnect', handleConnectionChange);
      TwilioVoiceService.callConnection.on('reconnecting', handleConnectionChange);
    }

    return () => {
      if (TwilioVoiceService.callConnection) {
        TwilioVoiceService.callConnection.off('disconnect', handleConnectionChange);
        TwilioVoiceService.callConnection.off('reconnecting', handleConnectionChange);
      }
    };
  }, [audioWaveformActive, isCallInProgress, initializeAudioAnalysis, cleanupAudioAnalysis]);

  return {
    microphoneLevel: audioLevels.microphone,
    phoneLevel: audioLevels.phone,
    combinedLevel: audioLevels.combined,
    frequencyData,
    isActive,
    error,
  };
};
